import {get, post, remove} from "../core/api";
import {
    ClientReportProgress,
    DchResponse,
    DisclaimerTableData,
    GenerateReportRequest,
    PersonaIdListResponse,
    ReportConfig,
    SendReportRequest
} from "./models/Reporting";
import {App_Conf} from "../core/app_conf";
import {releaseToggleApiClient} from "../ReleaseToggles/ReleaseToggleApiClient";


const parseJsonWithErrorHandling = async (response: Response, errorPrefix: string) => {
    try {
        return await response.json();
    } catch (e) {
        console.error(`Failed to parse JSON from ${errorPrefix}:`, e);
        throw e;
    }
};

const generateReport = async (reqBody: GenerateReportRequest, profileId: string): Promise<ClientReportProgress> => {
    const isAsyncReportingEnabled = await getFeatureFlagStatus("enableAsyncReporting");
    if (isAsyncReportingEnabled) {
        const response = await post(`/reporting/pdf-report/async`, reqBody);
        return parseJsonWithErrorHandling(response, 'generateReport');
    }

    const response = await post(`/advisory/reporting/generateReport/${profileId}`, reqBody);
    return parseJsonWithErrorHandling(response, 'generateReport');
}

export const getAsyncGeneratedReport = async (requestId: string): Promise<Blob> => {
    const response = await get(`/reporting/pdf-report/async/result/${requestId}`);
    return response.blob();
}

export const getAsyncReportingStatus = async (requestId: string): Promise<ClientReportProgress> => {
    const response = await get(`/reporting/pdf-report/async/status/${requestId}`);
    return parseJsonWithErrorHandling(response, 'getAsyncReportingStatus');
}

const getClientPersonaIds = async (profileId: string, resourceCode: string): Promise<PersonaIdListResponse> => {
    const response = await get(`/advisory/reporting/getClientPersonaList/${profileId}?resourceCode=${resourceCode}`);
    return parseJsonWithErrorHandling(response, 'getClientPersonaIds');
}

const getReportConfig = async (): Promise<ReportConfig> => {
    const response = await fetch(`${App_Conf.APP_URL}/advisory/reportConfig`);
    return parseJsonWithErrorHandling(response, 'getReportConfig');
}

const getBlobFromReportObjectUrl = async (generatedReportUrl: string): Promise<Blob> => {
    const response = await fetch(generatedReportUrl);
    return response.blob();
}

export const getDisclaimerData = async (): Promise<DisclaimerTableData> => {
    const response = await get(`/advisory/reporting/getDisclaimerData`);
    return parseJsonWithErrorHandling(response, 'getDisclaimerData');
}

export const getGeneratedReport = async (profileId: string): Promise<Blob> => {
    const response = await get(`/advisory/reporting/getClientReport/${profileId}`);
    return response.blob();
}

export const getReportingStatus = async (profileId: string): Promise<ClientReportProgress> => {
    const response = await get(`/advisory/reporting/getReportStatus/${profileId}`);
    return parseJsonWithErrorHandling(response, 'getReportingStatus');
}

const sendReportToDCH = async (profileId: string, reqBody: SendReportRequest): Promise<DchResponse> => {
    const response = await post(`/advisory/reporting/sendClientReport/${profileId}`, reqBody);
    return parseJsonWithErrorHandling(response, 'sendReportToDCH');
}

const deleteClientReport = async (profileId: string): Promise<String> => {
    const response = await remove(`/advisory/reporting/deleteClientReport/${profileId}`);
    return parseJsonWithErrorHandling(response, 'deleteClientReport');
}

const getFeatureFlagStatus = async (featureFlag: string): Promise<boolean> => {
    try {
        return await releaseToggleApiClient.getLaunchDarklyFlag(featureFlag);
    } catch (error) {
        console.error(`Failed to retrieve feature flag in getFeatureFlagStatus:`, error);
        throw error;
    }
}

export const reportingApiClient = {
    generateReport,
    getAsyncGeneratedReport,
    getAsyncReportingStatus,
    getReportConfig,
    getClientPersonaIds,
    getBlobFromReportObjectUrl,
    getDisclaimerData,
    getGeneratedReport,
    getReportingStatus,
    sendReportToDCH,
    deleteClientReport,
    getFeatureFlagStatus
};
